"use client";

import { GotoRegister } from "@/app/[lang]/login/goto.register";
import { useTranslation } from "@/i18n/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { getPublicEnv } from "@tucan/utils";
import { App, Button, Form, Input, Result, Spin } from "antd";
import { LoginWithGoogleButton } from "components/auth/login.with.google.button";
import { captureException } from '@tucan/utils/error-tracking'
import { useLoginByEmailMutation } from "graphql/auth/login.by.email.mutation";
import { useApiProxy } from "hooks/use.api.proxy";
import { useRegisterActivity } from "hooks/use.register.activity";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";
import { Trans } from "react-i18next";

interface LoginForm {
  email: string;
  password?: string;
}

export const Login = () => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const [form] = Form.useForm<LoginForm>();
  const [submitting, setSubmitting] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const registerActivity = useRegisterActivity();
  const [challenge, setChallenge] = useState<string>();
  const { message } = App.useApp();

  useEffect(() => {
    void registerActivity("LOGIN_IDENTIFY");
  }, []);

  const [loginByEmail] = useLoginByEmailMutation();
  const { trigger } = useApiProxy("/auth/password");

  const loginWithPassword = useCallback(async (e: LoginForm) => {
    setSubmitting(true);

    try {
      await trigger({
        data: {
          email: e.email,
          password: e.password,
        },
        method: "POST",
      });
      void message.success(t("you-logged-in", "You are now logged in!"));

      void registerActivity("LOGIN", undefined, {
        with: "password",
      });
      router.push(`/${i18n.language}/app`);
    } catch (e) {
      const errors = e.response?.data?.errors || [];

      errors.forEach((error) => {
        switch (error.message) {
          case "ERROR_EMAIL":
            void message.error(
              t(
                "password-login-failed",
                "There is something wrong with your password.",
              ),
            );
            break;
          case "PASSWORD_EXPIRED":
            void message.error(
              t(
                "password-login-expired-renew",
                "Your password expired, please set a new one.",
              ),
            );
            router.push(
              `/login/change-password?token=${error.extensions.originalError.error}`,
            );
            break;
        }
      });
      setSubmitting(false);
    }
  }, []);

  const loginWithEmail = useCallback(
    async (e: LoginForm) => {
      setSubmitting(true);

      const redirect = `${window.location.protocol}//${window.location.host}/login/pending?token=`;

      try {
        const response = await loginByEmail({
          variables: {
            email: e.email,
            redirect,
            verifyWithPassword: !challenge,
          },
        });

        if (response.data?.status.message !== "CHALLENGE_EMAIL") {
          setSubmitting(false);
          setChallenge(response.data.status.message);
          return;
        }

        void message.success(
          t("email-sent-successfully", "E-Mail sent, check your inbox!"),
        );

        router.push("/login/pending");
        void registerActivity("LOGIN_SENT");
      } catch (e) {
        if (CombinedGraphQLErrors.is(e)) {
          e.errors.forEach((error) => {
            switch (error.message) {
              case "ERROR_EMAIL":
                void message.error(
                  t(
                    "user-load-failed",
                    "Could not find user, do you want to register?",
                  ),
                );
                break;

              case "ERROR_REDIRECT":
                void message.error("Invalid Redirect Path");
                break;
            }
          });
        }
      }
      setSubmitting(false);
    },
    [router, challenge],
  );

  useEffect(() => {
    try {
      localStorage.setItem("test-field", "test-value");
      localStorage.removeItem("test-field");
      setStorageAvailable(true);
    } catch (e) {
      void message.error(
        t(
          "local-storage-unavailable",
          "No local storage available. Cannot login",
        ),
      );
      captureException(e);
      setStorageAvailable(false);
    }
  }, []);

  if (challenge) {
    switch (challenge) {
      case "CHALLENGE_PASSWORD":
        return (
          <>
            <h1>
              {t("login-challenge-password", "Please enter your password")}
            </h1>
            <Spin spinning={submitting}>
              <Form
                form={form}
                onFinish={(data) => void loginWithPassword(data)}
                layout={"vertical"}
              >
                <Form.Item name="email" label={"Email"}>
                  <Input disabled={true} size={"large"} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label={"Password"}
                  rules={[
                    {
                      message: t("password-required", "password required"),
                      required: true,
                    },
                  ]}
                >
                  <Input.Password size={"large"} autoFocus />
                </Form.Item>

                <Form.Item>
                  <Button
                    disabled={!storageAvailable}
                    size="large"
                    type="primary"
                    htmlType="submit"
                    block
                  >
                    {t("login-check-password", "Login")}
                  </Button>

                  <Button
                    size={"large"}
                    type={"link"}
                    block
                    onClick={() =>
                      void loginWithEmail({
                        email: form.getFieldValue(["email"]),
                      })
                    }
                  >
                    {t(
                      "login-forgot-password",
                      "Send me an email login link instead",
                    )}
                  </Button>
                </Form.Item>
              </Form>
            </Spin>
          </>
        );

      case "CHALLENGE_ZITADEL":
        return void signIn(
          "zitadel",
          {},
          {
            login_hint: form.getFieldValue(["email"]),
            prompt: "login",
          },
        );
    }

    return (
      <Result
        status={"info"}
        title={t("login-unsupported-challenge", "Unsupported challenge")}
        extra={[
          <Button key={"reset"} onClick={() => setChallenge(undefined)}>
            {t("reset-unsupported-challenge", "Back to Login")}
          </Button>,
        ]}
      ></Result>
    );
  }

  return (
    <>
      <h1>{t("member-login")}</h1>

      <Spin spinning={submitting}>
        <Form
          form={form}
          onFinish={(data) => void loginWithEmail(data)}
          layout={"vertical"}
        >
          <Form.Item
            name="email"
            rules={[
              {
                message: t("email-required", "email required"),
                required: true,
              },
              {
                message: t("invalid-email", "not a valid email"),
                type: "email",
              },
            ]}
            label={"Email"}
          >
            <Input
              autoFocus
              disabled={!storageAvailable}
              placeholder={t("email-placeholder", "please enter your email")}
              size={"large"}
            />
          </Form.Item>

          <p
            style={{
              color: "#B9B9B9",
              fontSize: 12,
              lineHeight: "20px",
              marginBottom: 16,
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "80%",
              textAlign: "center",
            }}
          >
            <Trans i18nKey={"login-recaptcha"}>
              1
              <a href={"/tos"} target={"_blank"} rel="noopener noreferrer">
                2
              </a>{" "}
              3
              <a
                href={getPublicEnv("PRIVACY_AGREEMENT_URL")}
                target={"_blank"}
                rel="noopener noreferrer"
              >
                4
              </a>
              5
            </Trans>
          </p>

          <Form.Item>
            <Button
              disabled={!storageAvailable}
              size="large"
              type="primary"
              htmlType="submit"
              block
            >
              {t("sign-in")}
            </Button>

            <Suspense>
              <GotoRegister />
            </Suspense>
          </Form.Item>
          <Form.Item>
            <LoginWithGoogleButton
              disabled={!storageAvailable}
              onSubmitting={setSubmitting}
              onSuccess={() => {
                void router.push("/app");
              }}
            />
          </Form.Item>
        </Form>
      </Spin>
    </>
  );
};
